import Drawer from "@mui/material/Drawer";
import { Button, LoadingSpinner, Tab, TabsNavigation } from "pepsico-ds";
import PropTypes from "prop-types";
import { useContext, useState } from "react";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import {
  getDateWithFormat,
  getUploadedCodeGroupFileDetails,
  returnPlaceHolderIfEmpty,
} from "../../utils/commons";
import {
  CODE_GROUP_CREATION_TYPE,
  CODE_GROUP_CREATION_TYPE_LABELS,
} from "../../utils/constants";
import CodeGroupExportJobs from "./CodeGroupExportJobs";
import "./codeGroupDetailsDrawer.scss";

const Field = ({ label, value, children }) => (
  <div>
    {children ? (
      children
    ) : (
      <div className="codegroup-details-field-value display-flex flex-direction-column">
        <span className="codegroup-details-field-label">{label}</span>
        <span>{returnPlaceHolderIfEmpty(value, "-")}</span>
      </div>
    )}
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="codegroup-details-section-title">{title}</div>
    <div className="codegroup-details-fields">{children}</div>
  </div>
);

const formatMaxUsageCount = (count) =>
  count !== undefined && count !== null ? count.toLocaleString() : "Unlimited";

const CodeGroupDetailsDrawer = ({
  showDrawer,
  onCloseDrawer,
  codeGroupData,
  loadCodeGroupLoader,
  handleActionButtons,
}) => {
  const { programTimezone } = useContext(GlobalConfigContext);
  const [selectedTab, setSelectedTab] = useState(0);

  const isUploadedType =
    codeGroupData?.creationType === CODE_GROUP_CREATION_TYPE.PRE_GENERATED;

  const handleTabChange = (index) => {
    setSelectedTab(index);
  };

  const basicDetailsSection = (
    <Section>
      <Field label="Code Group Name" value={codeGroupData?.codeGroupName} />
      <Field label="Status" value={codeGroupData?.lifecycleStatus} />
      <Field label="Type" value={codeGroupData?.category} />
      <Field label="Code Count" value={codeGroupData?.codesGeneratedCount} />
      {isUploadedType && (
        <Field
          label="Max Usage"
          value={formatMaxUsageCount(codeGroupData?.codesMaxUsageCount)}
        />
      )}
      <Field label="Used Code Count" value={codeGroupData?.codesUsedCount} />
      <Field
        label="Creation Type"
        value={
          CODE_GROUP_CREATION_TYPE_LABELS[codeGroupData?.creationType] ??
          codeGroupData?.creationType
        }
      />
      <Field
        label="Start Date"
        value={
          codeGroupData?.codeActiveStartDate && programTimezone
            ? getDateWithFormat(codeGroupData.codeActiveStartDate, programTimezone)
            : null
        }
      />
      <Field
        label="End Date"
        value={
          codeGroupData?.codeActiveEndDate && programTimezone
            ? getDateWithFormat(codeGroupData.codeActiveEndDate, programTimezone)
            : null
        }
      />
    </Section>
  );

  const uploadedExtraSection = (
    <>
      <Section>
        <Field label="File Name">
          {codeGroupData?.fileDetails?.length ? (
            <div className="codegroup-details-field-value">
              <div className="codegroup-details-field-label">File Name</div>
              {codeGroupData.fileDetails.map((file) => (
                <div key={file.id} title="Click to download file">
                  {getUploadedCodeGroupFileDetails({
                    fileDetails: [file],
                  }).fileName || file?.name}
                </div>
              ))}
              <div className="display-flex justify-content-end mt-2">
                <Button
                  disabled={!(codeGroupData?.exportFile?.length > 0)}
                  onClick={() => handleActionButtons("export", codeGroupData)}
                  variant="secondary"
                  size={"medium"}
                >
                  Download
                </Button>
              </div>
            </div>
          ) : (
            <div className="codegroup-details-field-value">No files available.</div>
          )}
        </Field>
      </Section>
    </>
  );

  const setupAndProducerSection = (
    <div className="codegroup-details-drawer-content">
      {basicDetailsSection}
      <Section>
        <Field label="Brand" value={codeGroupData?.brand} />
        <Field label="Producer Company" value={codeGroupData?.producerCompany} />
        <Field label="Codes Printed" value={codeGroupData?.codesPrintedCount} />
        <Field label="Product Volume" value={codeGroupData?.productVolume} />
        <Field label="Package Type" value={codeGroupData?.packageType} />
        <Field label="Product Cap Color" value={codeGroupData?.productCapColor} />
      </Section>
    </div>
  );

  const specificationsSection = (
    <div className="codegroup-details-drawer-content">
      <Section>
        <Field label="Fixed Code Length" value={codeGroupData?.codeLength} />
        <Field label="Prefix Value Used" value={codeGroupData?.prefixVal} />
        <Field label="Suffix Value Used" value={codeGroupData?.suffixVal} />
        <Field
          label="Character Excluded From Code"
          value={codeGroupData?.excludeCharacterValues}
        />
        <Field label="Dummy Code" value={codeGroupData?.dummyCodeMessage} />
        <Field
          label="Possibility Of Getting Dummy Code"
          value={
            typeof codeGroupData?.dummyCodePercent === "number"
              ? `${codeGroupData.dummyCodePercent}%`
              : codeGroupData?.dummyCodePercent
          }
        />
      </Section>
    </div>
  );

  return (
    <Drawer
      open={showDrawer}
      onClose={onCloseDrawer}
      anchor="right"
      classes={{ paper: "codegroup-details-drawer" }}
    >
      <div className="p-3">
        <p className="font-xl font-bold">Code Group Details</p>
      </div>
      {loadCodeGroupLoader && (
        <LoadingSpinner
          data-testid="loading-spinner"
          size={{ height: "24px", width: "24px" }}
          className="m-3"
        />
      )}

      {!loadCodeGroupLoader &&
        (isUploadedType ? (
          <div className="codegroup-details-drawer-content">
            {basicDetailsSection}
            {uploadedExtraSection}
          </div>
        ) : (
          <>
            <TabsNavigation
              className="codegroup-details-tabs"
              style={{ borderBottom: "2px solid #D7E5F8", overflowX: "unset" }}
              onUpdate={handleTabChange}
              selectedIndex={selectedTab}
            >
              <Tab label="Setup and Producer" />
              <Tab label="Specifications" />
              <Tab label="View File History" />
            </TabsNavigation>
            {selectedTab === 0 && setupAndProducerSection}
            {selectedTab === 1 && specificationsSection}
            {selectedTab === 2 && (
              <CodeGroupExportJobs codeGroupId={codeGroupData?.codeGroupId} />
            )}
          </>
        ))}
    </Drawer>
  );
};

CodeGroupDetailsDrawer.propTypes = {
  showDrawer: PropTypes.bool.isRequired,
  onCloseDrawer: PropTypes.func.isRequired,
  codeGroupData: PropTypes.object,
  loadCodeGroupLoader: PropTypes.bool.isRequired,
  handleActionButtons: PropTypes.func.isRequired,
};

export default CodeGroupDetailsDrawer;
